import { cssMerge } from '@volvo-cars/css/utils';
import type React from 'react';
import { useRef } from 'react';
import { combineIds } from './aria-utils';
import type { GlobalHTMLAttributes } from './types';
import { useIds } from './use-ids';
export interface BaseSelectableCardProps extends Omit<
  GlobalHTMLAttributes,
  'title'
> {
  /**
   * Main title or label for the card.
   */
  title: string;

  /**
   * Optional metadata (e.g., pricing, status).
   */
  meta?: string;

  /**
   * Optional description text.
   */
  description?: string;

  /**
   * Optional image configuration.
   * Can be either an object with image properties or a custom React element (e.g., Next.js Image).
   * When using a custom element, ensure it respects the 52px height constraint applied by CSS.
   */
  image?:
    | {
        /**
         * Image source URL.
         */
        src: string;
        /**
         * Alt text for the image.
         */
        alt?: string;
        /**
         * Loading strategy for the image.
         * @default 'lazy'
         */
        loading?: 'lazy' | 'eager';
        /**
         * Aspect ratio of the image.
         * @default '1:1'
         */
        aspectRatio?: '1:1' | '16:9';
        /**
         * Error handler for when the image fails to load.
         */
        onError?: React.ReactEventHandler<HTMLImageElement>;
      }
    | React.ReactElement;

  /**
   * Custom content that renders below the description.
   * Use this for advanced layouts or interactive elements.
   */
  children?: React.ReactNode;

  /**
   * ARIA labelledby for the input.
   * Only use this when you need to reference multiple elements for the label.
   * The native <label> element already provides proper association.
   */
  'aria-labelledby'?: string;

  /**
   * ARIA describedby for the input.
   * This will be combined with auto-generated IDs for description and meta content.
   */
  'aria-describedby'?: string;
}

export interface SelectableCardBaseRenderProps {
  inputId: string;
  labelId: string;
  effectiveAriaLabelledby: string | undefined;
  effectiveAriaDescribedby: string | undefined;
  inputRef: React.RefObject<HTMLInputElement | null>;
}

interface SelectableCardBaseProps extends BaseSelectableCardProps {
  /**
   * Render function that receives IDs and should return the input element.
   * @internal
   */
  renderInput: (props: SelectableCardBaseRenderProps) => React.ReactNode;
}

/**
 * Internal base component for selectable cards.
 * Handles common rendering logic for card wrapper and content slots.
 * @internal
 */
export function SelectableCardBase({
  title,
  meta,
  description,
  image,
  children,
  id,
  className,
  hidden,
  dir,
  lang,
  translate,
  slot,
  style,
  'aria-labelledby': ariaLabelledby,
  'aria-describedby': ariaDescribedby,
  renderInput,
}: SelectableCardBaseProps) {
  const { inputId, labelId, descriptionId, metaId } = useIds(id);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Only use aria-labelledby if explicitly provided (native label is preferred)
  const effectiveAriaLabelledby = ariaLabelledby;

  // Build aria-describedby combining user-provided and auto-generated IDs
  const effectiveAriaDescribedby = combineIds(
    ariaDescribedby,
    Boolean(description) && descriptionId,
    Boolean(meta) && metaId,
  );

  // Handle clicks on the card (excluding interactive elements)
  const handleCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    // Check if the click target is or is inside an interactive element or label
    const isInteractive = target.closest(
      'button, a, input, select, textarea, label, [role="button"], [role="link"], [tabindex]:not([tabindex="-1"])',
    );

    // If not clicking an interactive element, trigger the input
    if (!isInteractive && inputRef.current) {
      inputRef.current.click();
    }
  };

  return (
    // oxlint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- The click delegates to the nested native input; keyboard interaction stays on that input and its label.
    <div
      id={id}
      className={cssMerge('selectable-card', className)}
      hidden={hidden}
      dir={dir}
      lang={lang}
      translate={translate}
      slot={slot}
      style={style}
      onClick={handleCardClick}
    >
      {renderInput({
        inputId,
        labelId,
        effectiveAriaLabelledby,
        effectiveAriaDescribedby,
        inputRef,
      })}

      {image &&
        (typeof image === 'object' && 'src' in image ? (
          <img
            slot="image"
            src={image.src}
            alt={image.alt ?? ''}
            loading={image.loading ?? 'lazy'}
            onError={image.onError}
            aria-hidden={image.alt ? undefined : 'true'}
            className={
              image.aspectRatio === '16:9' ? 'aspect-16/9' : 'aspect-1/1'
            }
          />
        ) : (
          <div slot="image">{image}</div>
        ))}

      <label htmlFor={inputId} id={labelId} slot="title">
        {title}
      </label>

      {meta && (
        <div slot="meta" id={metaId}>
          {meta}
        </div>
      )}

      {description && (
        <div
          slot="description"
          id={descriptionId}
          className={children ? 'mb-8' : undefined}
        >
          <p>{description}</p>
        </div>
      )}

      {children}
    </div>
  );
}
